import { useElementSize } from '@mantine/hooks'
import { sortByKey } from '@semios/app-platform-common'
import { SettingsDeleteButton } from 'App/Map/UserSettingsMenu/components/SettingsDeleteButton/SettingsDeleteButton'
import { SettingsDuplicateButton } from 'App/Map/UserSettingsMenu/components/SettingsDuplicateButton/SettingsDuplicateButton'
import { SettingsEditButton } from 'App/Map/UserSettingsMenu/components/SettingsEditButton/SettingsEditButton'
import { translate } from 'i18n/i18n'
import type { Dispatch, SetStateAction } from 'react'
import { colors } from 'settings/colors'
import { apiFetch } from 'utils/apiFetch'
import { calculateTextWidth } from 'utils/calculateTextWidth'
import { showNotification } from 'utils/showNotification'
import { showConfirmSettingModal } from '../../../_utils/showConfirmSettingModal'
import type { Alert, AlertWithPossibleId } from '../../_types'
import type { TModalDrawerForm } from '../../Alerts'
import { mapAlertIdTypeToLabel } from '../../mapAlertIdTypeToLabel'
import { ContactsRow } from './ContactsRow/ContactsRow'
import { CoverageColumn } from './CoverageColumn/CoverageColumn'
import { FrequencyAndScheduling } from './FrequencyAndScheduling/FrequencyAndScheduling'
import { RulesRow } from './RulesRow/RulesRow'
import { WeatherForecastRulesRow } from './WeatherForecastRulesRow/WeatherForecastRulesRow'

export const AlertCard = ({
  alert,
  setSelectedAlert,
  setModalDrawerForm,
  postDeleteHandler,
}: {
  alert: Alert
  setSelectedAlert: Dispatch<SetStateAction<AlertWithPossibleId | null>>
  setModalDrawerForm: Dispatch<SetStateAction<TModalDrawerForm>>
  postDeleteHandler: (alertId: Alert['id']) => void
}) => {
  const { name, contacts, groups, type, properties, startDate, endDate, id } = alert
  const { ref: coverageRef, width: coverageWidth } = useElementSize()
  const { ref: alertCardRef, width: alertCardWidth } = useElementSize()

  const preDeleteAlert = () => {
    showConfirmSettingModal({
      title: translate.phrases.banyanApp('Delete Alert'),
      content: (
        <div>
          <p>
            {translate.phrases.banyanApp('Are you sure you want to delete {{alertName}} as an alert?', {
              alertName: name,
            })}
          </p>
        </div>
      ),
      onConfirm: () => handleDeleteAlert(id, type.id === 'weatherForecastAlert'),
      confirmButtonText: translate.phrases.banyanApp('Delete Alert'),
    })
  }

  const handleDeleteAlert = async (id: string, isWeatherForecastAlert: boolean) => {
    try {
      if (isWeatherForecastAlert) {
        await apiFetch({
          url: '/legacy-weather-forecast-alerts-management',
          body: {
            legacyWeatherForecastAlertsToDelete: [Number(id)],
          },
        })
      } else {
        await apiFetch({
          url: '/legacy-v3-alerts-management',
          body: {
            legacyV3AlertsToDelete: [id],
          },
        })
      }

      showNotification({
        type: 'success',
        message: translate.phrases.banyanApp('Alert successfully deleted'),
      })

      postDeleteHandler(id)
    } catch (error) {
      showNotification({
        type: 'error',
        message: translate.phrases.banyanApp('Failed to delete alert'),
      })
    }
  }

  // Mantine 8's useElementSize reports the border box: take off the card's padding and border
  const alertCardContentWidth = alertCardWidth - 2 * (15 + 1)
  const buttonsShouldGoToNextLine = alertCardContentWidth - coverageWidth < 380

  return (
    <div
      css={{
        border: `1px solid ${colors.grey200}`,
        padding: 15,
        marginBottom: 5,
        color: colors.midnight,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
      }}
      ref={alertCardRef}
    >
      <AlertHeader name={name} groups={groups} contacts={contacts} widthAvailable={alertCardContentWidth} />
      <div css={{ margin: '20px 0' }}>
        <Rules alert={alert} />
        <FrequencyAndScheduling
          sleepMinutes={('sleepMinutes' in alert && alert.sleepMinutes) || null}
          frequencyMinutes={('frequencyMinutes' in alert && alert.frequencyMinutes) || null}
          startDate={startDate}
          endDate={endDate}
          hourOfDay={('hourOfDay' in alert && alert.hourOfDay) || undefined}
        />
      </div>
      <div css={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <div>{`${translate.phrases.banyanApp('Type')}:`}</div>
        <div css={{ fontWeight: '700' }}>{mapAlertIdTypeToLabel(type.id)}</div>
      </div>

      <div
        css={{
          display: 'flex',
          flexDirection: buttonsShouldGoToNextLine ? 'column' : 'row',
          justifyContent: buttonsShouldGoToNextLine ? 'left' : 'space-between',
        }}
      >
        <div css={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div>{`${translate.phrases.banyanApp('Coverage')}:`}</div>
          <div ref={coverageRef}>
            <CoverageColumn propertyIds={properties?.map((p) => Number(p.id))} />
          </div>
        </div>
        <div
          css={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            marginTop: buttonsShouldGoToNextLine ? 20 : 0,
          }}
        >
          <SettingsDeleteButton
            onClick={() => {
              preDeleteAlert()
            }}
          />
          <SettingsEditButton
            onClick={() => {
              setSelectedAlert(alert)

              setModalDrawerForm('editAlert')
            }}
          />
          <SettingsDuplicateButton
            onClick={() => {
              setSelectedAlert({ ...alert, id: undefined })

              setModalDrawerForm('editAlert')
            }}
          />
        </div>
      </div>
    </div>
  )
}

const AlertHeader = ({
  name,
  groups,
  contacts,
  widthAvailable,
}: Pick<Alert, 'name' | 'groups' | 'contacts'> & { widthAvailable: number }) => {
  const nameStyles = { fontSize: '16px', fontWeight: '700' }
  const { ref: contactRef, width: contactWidth } = useElementSize()
  const nameTextWidth = calculateTextWidth({ ...nameStyles, text: name })
  const nameShouldGoToNextLine = widthAvailable - contactWidth - 20 < nameTextWidth
  const sortedContactNames = contacts?.sort(sortByKey('name')).map((c) => c.name) || []
  const sortedGroupNames = groups?.sort(sortByKey('name')).map((g) => g.name) || []
  const groupAndContactNameList = [...sortedContactNames, ...sortedGroupNames]

  return nameShouldGoToNextLine ? (
    <>
      <div css={{ marginLeft: 10, display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <ContactsRow contactNameList={groupAndContactNameList} />
      </div>
      <div css={{ ...nameStyles }}>{name}</div>
    </>
  ) : (
    <div
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <div css={{ ...nameStyles }}>{name}</div>

      <div css={{ marginLeft: 10 }}>
        <div ref={contactRef}>
          <ContactsRow contactNameList={groupAndContactNameList} />
        </div>
      </div>
    </div>
  )
}

const Rules = ({ alert }: { alert: Alert }) => {
  if (alert.type.id === 'weatherForecastAlert' && 'hourOfDay' in alert) {
    return <WeatherForecastRulesRow rules={alert.rules} />
  }

  if (alert.type.id !== 'weatherForecastAlert' && Array.isArray(alert.rules) && 'insect' in alert) {
    return (
      <RulesRow
        rules={alert.rules}
        alertTypeId={alert.type.id}
        insectId={alert.insect?.id ? Number(alert.insect.id) : undefined}
      />
    )
  }

  return null
}
